import React, { useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  pasteFilesFromClipboard,
  removeFilesFromNode,
  reorder,
  selectStartToEnd,
  toggleSortDesigns,
  updateFileProps,
  updateFilesInNode,
} from "../../utils";
import { filesClipboardState, designListState, apiKeyState, toastState } from "../../recoil/filemanager/atoms";
import { DraggableFileNode } from "./FileNode";
import { ContextMenu, Popover } from "@blueprintjs/core";
import { baseUrl, clipboardOperations, formatClipboardOperations } from "../../constants";
import { FileManagerAPI, AppProviderAPI } from "../../api";
import FmContextMenu from "./FmContextMenu";
import Breadcrumbs from "./Breadcrumbs";
import classNames from "classnames";
import { DeleteEntryDialog } from "./AlertDialogs";
import { Button, H6, Menu, MenuItem } from "@alternative/components";
import { IconNames } from "@alternative/icons";
const ThumbNode = (props) => {
  const { file, isSelected } = props;
  return (
    <div className={classNames("file-list-thumb", { "file-list-thumb--active": isSelected })}>
      <div>
        <img src={`${baseUrl}${file.thumbUrl}`} alt={file.name} />
      </div>
      <H6>{file.name}</H6>
    </div>
  );
};
const FileList = () => {
  const [isDragging, setIsDragging] = useState(false);
  const apiKey = useRecoilValue(apiKeyState);
  const [designlist, setDesignlist] = useRecoilState(designListState);
  const [clipBoardItems, setClipboardItems] = useRecoilState(filesClipboardState);
  const { designTree, selectedFolders, selectedDesigns } = designlist;
  const { files } = selectedFolders[0];
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false });

  const [toastListState, setToastLisState] = useRecoilState(toastState);
  const { toasts } = toastListState;
  const onDragEnd = async (result) => {
    setIsDragging(false);
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reFiles = reorder(files, result.source.index, result.destination.index);
    const updatedTree = updateFilesInNode(designTree, selectedFolders[0], reFiles);
    const fileOrderFolders = selectedFolders[0].children.map((item) => item.fullPath);
    const fileOrderFiles = reFiles.map((item) => item.fullPath);
    const fileOrder = [...fileOrderFolders, ...fileOrderFiles];
    setDesignlist({
      ...designlist,
      designTree: updatedTree,
      selectedFolders: [{ ...selectedFolders[0], files: reFiles }],
    });
    await FileManagerAPI.setFileOrder({ key: apiKey, path: selectedFolders[0].fullPath, fileOrder });
  };
  const handleDesignClick = async (design, e) => {
    e.preventDefault();
    if (selectedDesigns.length === 1 && selectedDesigns[0].id === design.id) return;
    let selDesigns = [design];
    if (e.ctrlKey) {
      const selection = [...selectedDesigns, design];
      selDesigns = files.filter((fileNode) => selection.find((item) => item.id === fileNode.id));
    }
    if (e.shiftKey) {
      if (!selectedDesigns.length) {
        selDesigns = [design];
      } else {
        const { files } = selectedFolders[0];
        selDesigns = selectStartToEnd({ list: files, startNode: selectedDesigns[0], endNode: design });
      }
    }
    setDesignlist({ ...designlist, selectedDesigns: selDesigns, selectedVariations: [] });
    //TODO: acync action.
    if (selDesigns.length && !selDesigns[0].thumbUrl) {
      const newSelDesigns = [...selDesigns];
      const designsWithThumb = await AppProviderAPI.getDesignThumb(apiKey, [newSelDesigns[0]]);
      newSelDesigns[0] = designsWithThumb[0];
      setDesignlist({
        ...designlist,
        selectedDesigns: newSelDesigns,
        selectedVariations: [],
      });
    }
  };
  const handleCopy = (e) => {
    setClipboardItems({ ...clipBoardItems, items: selectedDesigns, operation: clipboardOperations.COPY, type: "file" });
  };
  const handleCut = (e) => {
    setClipboardItems({ ...clipBoardItems, items: selectedDesigns, operation: clipboardOperations.CUT, type: "file" });
  };
  const handlePaste = async (e) => {
    let clipboardFiles = [...clipBoardItems.items];
    let message = `${formatClipboardOperations(clipBoardItems.operation)} ${clipboardFiles.length} files`;
    let newToast = {
      message,
      canDismiss: false,
    };
    setToastLisState({
      ...toastListState,
      toasts: [...toasts, newToast],
    });
    const fileMap = clipboardFiles.map((clipboardFile) => ({
      OldFile: clipboardFile.fullPath,
      NewFile: `${selectedFolders[0].fullPath}/${clipboardFile.filename}`,
    }));
    let done = false;
    if (clipBoardItems.operation === clipboardOperations.COPY) {
      done = await FileManagerAPI.copyFileList({ key: apiKey, fileMap });
    }
    if (clipBoardItems.operation === clipboardOperations.CUT) {
      done = await FileManagerAPI.moveFileList({ key: apiKey, fileMap });
    }
    if (!done) return;
    const { updatedNode, updatedTree } = pasteFilesFromClipboard(
      designTree,
      clipboardFiles,
      selectedFolders[0],
      clipBoardItems.operation
    );

    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDesignlist({ ...designlist, designTree: updatedTree, selectedFolders: selFolders });
    message = `${formatClipboardOperations(clipBoardItems.operation, true)} ${clipboardFiles.length} files`;
    setToastLisState({
      ...toastListState,
      toasts: [...toasts, { message, canDismiss: true }],
    });
  };
  const handleDelete = async (e) => {
    if (!selectedDesigns) return;
    let deletedFiles = [];
    for (let file of selectedDesigns) {
      if (file.variations && file.variations.vars) {
        for (let variation of file.variations.vars) {
          await FileManagerAPI.deleteFile({ key: apiKey, file: variation.fullPath });
        }
        await FileManagerAPI.deleteFolder({ key: apiKey, path: file.variations.vars[0].location });
      }
      const del = await FileManagerAPI.deleteFile({ key: apiKey, file: file.fullPath });
      if (del) deletedFiles.push(file);
    }

    const { updatedTree, updatedNode } = removeFilesFromNode({
      designTree,
      parentNode: selectedFolders[0],
      files: deletedFiles,
    });
    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDeleteDialog({ ...deleteDialog, isOpen: false });
    setDesignlist({ ...designlist, designTree: updatedTree, selectedFolders: selFolders, selectedDesigns: [] });
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    ContextMenu.show(
      <FmContextMenu
        handleCut={handleCut}
        handleCopy={handleCopy}
        handlePaste={handlePaste}
        handleDelete={() => setDeleteDialog({ ...deleteDialog, isOpen: true })}
        pasteEnabled={clipBoardItems.items.length && clipBoardItems.type === "file"}
        deleteEnabled={selectedDesigns.length}
      />,
      { left: e.clientX, top: e.clientY },
      () => {}
    );
  };
  const handleChangeName = async (file, val) => {
    const ren = await FileManagerAPI.renameFile({ key: apiKey, oldfile: file.fullPath, newfilename: val });
    if (!ren) return;
    const { updatedTree, updatedNode } = updateFileProps(designTree, file, "name", val);
    const updatedDesign = updatedNode.files.find((item) => item.id === file.id);
    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDesignlist({
      ...designlist,
      designTree: updatedTree,
      selectedFolders: selFolders,
      selectedDesigns: [{ ...updatedDesign, thumbUrl: selectedDesigns[0].thumbUrl, props: selectedDesigns[0].props }],
    });
  };
  const handleSort = (ascending) => {
    const { updatedTree, updatedFolder } = toggleSortDesigns({
      designTree,
      selectedFolder: selectedFolders[0],
      ascending,
    });
    const fileOrder = [
      ...updatedFolder.children.map((item) => item.fullPath),
      ...updatedFolder.files.map((item) => item.fullPath),
    ];
    FileManagerAPI.setFileOrder({ key: apiKey, path: updatedFolder.fullPath, fileOrder });
    setDesignlist({ ...designlist, designTree: updatedTree, selectedFolders: [updatedFolder], selectedDesigns: [] });
  };
  const renderListView = () => {
    return (
      <DragDropContext onDragEnd={onDragEnd} onBeforeDragStart={() => setIsDragging(true)}>
        <Droppable droppableId="list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <div
                onContextMenu={handleContextMenu}
                className={classNames("file-list-container", {
                  "file-list-container--dragging": isDragging,
                })}
              >
                {files.map((file, i) => (
                  <DraggableFileNode
                    key={file.id}
                    file={file}
                    index={i}
                    isSelected={selectedDesigns.find((item) => item.id === file.id)}
                    onClick={(e) => handleDesignClick(file, e)}
                    handleChangeName={(val) => handleChangeName(file, val)}
                  />
                ))}
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };
  const renderThumbView = () => {
    return (
      <div className="file-list-container thumb-view">
        {files.map((file, i) => (
          <ThumbNode key={i} isSelected={selectedDesigns.find((item) => item.id === file.id)} file={file} />
        ))}
      </div>
    );
  };
  return (
    <>
      <div className="filelist-static-toolbar">
        <div className="filelist-sel-toolbar">
          {/* <Button text="Thumb View" minimal icon={IconNames.APP_MEDIUM_THUMBS} active />
          <Button text="List View" minimal icon={IconNames.LIST} /> */}
          <Popover
            minimal
            boundary="viewport"
            position="bottom-right"
            content={
              <Menu>
                <MenuItem text="Ascending (A-Z)" onClick={() => handleSort(true)} />
                <MenuItem text="Descending (Z-A)" onClick={() => handleSort(false)} />
              </Menu>
            }
          >
            <Button minimal icon={IconNames.SORT_A_Z} />
          </Popover>
        </div>
        <Breadcrumbs />
        <div className="filelist-sel-toolbar at-text-small" style={{ paddingLeft: "1rem" }}>
          {files.length} files in this folder
        </div>
      </div>
      {renderListView()}
      {/* {renderThumbView()} */}
      <DeleteEntryDialog
        isOpen={deleteDialog.isOpen}
        items={selectedDesigns}
        onDelete={() => {
          handleDelete();
        }}
        onClose={() => {
          setDeleteDialog({ ...deleteDialog, isOpen: false });
        }}
      />
    </>
  );
};

export default FileList;
